export type ConditionalFieldPresence = {
  hasFixedTerm: boolean;
  hasOtherEmployment: boolean;
  includeAddressFields: boolean;
};

export function getFallbackPresence(): ConditionalFieldPresence {
  return {
    hasFixedTerm: true,
    hasOtherEmployment: true,
    includeAddressFields: false,
  };
}

export function derivePresenceFromSchema(schemaKeys: string[]): ConditionalFieldPresence {
  const hasFixedTerm = ['is_fixed_term', 'contract_end_date'].some(k => schemaKeys.includes(k));

  const hasOtherEmployment = schemaKeys.includes('has_other_employment');

  const includeAddressFields = ['other_employment_street', 'other_employment_house_no',
    'other_employment_postal_code', 'other_employment_city'].some(k => schemaKeys.includes(k));

  return { hasFixedTerm, hasOtherEmployment, includeAddressFields };
}

type ConditionalValues = {
  is_fixed_term?: boolean | string;
  contract_end_date?: string;
  fixed_term_reason?: string;
  has_other_employment?: boolean | string;
  other_employment_employer?: string;
  other_employment_street?: string;
  other_employment_house_no?: string;
  other_employment_postal_code?: string;
  other_employment_city?: string;
  other_employment_start_date?: string;
  other_employment_monthly_salary?: string;
};

export function validateConditionals(
  values: ConditionalValues,
  presence: ConditionalFieldPresence
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (presence.hasFixedTerm && String(values.is_fixed_term) === 'true') {
    if (!values.contract_end_date) {
      errors.contract_end_date = 'Bitte Enddatum angeben';
    }
  }

  if (presence.hasOtherEmployment && String(values.has_other_employment) === 'true') {
    if (!values.other_employment_employer) {
      errors.other_employment_employer = 'Pflichtfeld';
    }
    if (!values.other_employment_start_date) {
      errors.other_employment_start_date = 'Pflichtfeld';
    }
    if (!values.other_employment_monthly_salary) {
      errors.other_employment_monthly_salary = 'Pflichtfeld';
    }

    if (presence.includeAddressFields) {
      const addressFields = [
        'other_employment_street',
        'other_employment_house_no',
        'other_employment_postal_code',
        'other_employment_city'
      ];

      addressFields.forEach(fieldKey => {
        if (!values[fieldKey as keyof ConditionalValues]) {
          errors[fieldKey] = 'Pflichtfeld';
        }
      });
    }
  }

  return errors;
}

export function serializeConditionalValues(
  values: ConditionalValues,
  presence: ConditionalFieldPresence
): Record<string, any> {
  const overrides: Record<string, any> = {};

  // § 1 Befristung (Always-send Policy: immer alle Keys senden, auch wenn false)
  if (presence.hasFixedTerm) {
    const fixed = String(values.is_fixed_term) === 'true';
    overrides.is_fixed_term = fixed ? 'true' : 'false';
    overrides.contract_end_date = fixed ? (values.contract_end_date || '') : '';
    overrides.fixed_term_reason = fixed ? (values.fixed_term_reason || '') : '';
  }

  // § 7 Weitere Beschäftigung (Always-send Policy)
  if (presence.hasOtherEmployment) {
    const other = String(values.has_other_employment) === 'true';
    overrides.has_other_employment = other ? 'true' : 'false';
    overrides.other_employment_employer = other ? (values.other_employment_employer || '') : '';
    overrides.other_employment_start_date = other ? (values.other_employment_start_date || '') : '';
    overrides.other_employment_monthly_salary = other ? (values.other_employment_monthly_salary || '') : '';

    if (presence.includeAddressFields) {
      overrides.other_employment_street = other ? (values.other_employment_street || '') : '';
      overrides.other_employment_house_no = other ? (values.other_employment_house_no || '') : '';
      overrides.other_employment_postal_code = other ? (values.other_employment_postal_code || '') : '';
      overrides.other_employment_city = other ? (values.other_employment_city || '') : '';
    }
  }

  return overrides;
}
